<script setup lang="ts">
import { dayjs } from 'element-plus'
import { crazyGraffitiColorList, type CrazyGraffitiInfoRes } from '../data'
import { QuestionFilled } from '@element-plus/icons-vue'
import { nextTick, onUnmounted, ref, watch } from 'vue'
import { socketEmitAsync, socketOff, socketOn } from '@/utils/web-socket'
import { useUserStore } from '@/stores/modules/user'
import { useCommonStore } from '@/stores/modules/common'
import { fill } from 'lodash'
import { formatNumberWithCommas } from '@/utils'

const emits = defineEmits(['back'])

const commonStore = useCommonStore()
const userStore = useUserStore()

const sectionList = ref<number[]>(fill(Array(9), 0))
const isBegin = ref(false)
const isEnd = ref(false)
const answer = ref<number[]>()
const group = ref()
const winGroup = ref(0)
const drawTime = ref('')
const playerCount = ref(0)

async function getCrazyGraffitiInfo() {
  const res = await socketEmitAsync<CrazyGraffitiInfoRes>({
    type: 'getCrazyGraffitiInfo',
  })
  sectionList.value = res.sectionList
  isEnd.value = res.isEnd
  answer.value = res.answer
  group.value = res.group
  winGroup.value = res.winGroup
  playerCount.value = res.playerCount || 0
  drawTime.value = res.drawTime
  msgBoxScrollToEnd()
}
getCrazyGraffitiInfo()
async function joinCrazyGraffiti() {
  if (typeof group.value === 'number') return ElMessage.warning('您已参赛')
  if (dayjs().isAfter(dayjs().hour(18).minute(55))) return ElMessage.warning('该时间段不可参赛')
  if (userStore.gold < 200000) return ElMessage.warning('金币不足')
  ElMessageBox.confirm('<p class="affix">确定参加【疯狂涂鸦】吗？（花费 <span class="gold">200,000</span>金币）</p>', '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  }).then(async () => {
    await socketEmitAsync({ type: 'joinCrazyGraffiti' })
    group.value = 0
    userStore.gold -= 200000
    await getCrazyGraffitiInfo()
    ElMessage.success('参赛成功')
  })
}

const curColorIndex = ref(-1)
async function handleCrazyGraffitiDraw(index: number) {
  if (!group.value) {
    if (isBegin.value) return ElMessage.warning('您未报名参赛')
    else return ElMessage.warning('赛事未开始')
  }
  if (curColorIndex.value === -1) return ElMessage.warning('请先选择颜色')
  if (dayjs().isBefore(dayjs(drawTime.value))) return ElMessage.warning('涂鸦冷却中')
  await socketEmitAsync({ type: 'crazyGraffitiDraw', data: { index, colorIndex: curColorIndex.value } })
  drawTime.value = dayjs().format('YYYY-MM-DD HH:mm:ss')
  ElMessage.success('涂鸦成功')
}

const msgList = ref<string[]>([])
async function msgBoxScrollToEnd() {
  await nextTick()
  const messageBoxEl = document.getElementById('crazyGraffitiMsgBox')
  if (!messageBoxEl) return
  messageBoxEl.scrollTo({
    top: messageBoxEl.scrollHeight - messageBoxEl.clientHeight,
    behavior: 'smooth',
  })
}

socketOn({
  type: 'crazyGraffitiBegin',
  callBack: () => {
    getCrazyGraffitiInfo()
    msgList.value.push('赛事开始')
    msgBoxScrollToEnd()
  },
})
socketOn({
  type: 'crazyGraffitiDraw',
  callBack: (res: { index: number; colorIndex: number }) => {
    const { index, colorIndex } = res
    sectionList.value[index] = colorIndex
    msgList.value.push(
      `区域【${index + 1}】被涂鸦 <span style="display: inline-block; width: 20px; height: 10px; background-color: ${
        crazyGraffitiColorList[colorIndex - 1]
      };"></span>`,
    )
    msgBoxScrollToEnd()
  },
})
socketOn({
  type: 'crazyGraffitiEnd',
  callBack: () => {
    getCrazyGraffitiInfo()
    msgList.value.push('赛事结束')
    msgBoxScrollToEnd()
  },
})
watch(
  () => commonStore.isSocketReconnect,
  async (val) => {
    if (!val) return
    getCrazyGraffitiInfo()
  },
)
onUnmounted(() => {
  socketOff('crazyGraffitiBegin')
  socketOff('crazyGraffitiDraw')
  socketOff('crazyGraffitiEnd')
})
</script>

<template>
  <div class="crazy-graffiti">
    <h2>
      疯狂涂鸦
      <div class="help-info">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>疯狂涂鸦说明</h4>
            <p>
              1. 疯狂涂鸦每日<span class="info">19:00</span>开赛，您可在每日<span class="info">18:55</span>之前花费<span class="gold">200,000</span>
              金币报名参赛。
            </p>
            <p>
              2.
              开赛时，参赛玩家将被随机分为两组（人数不一定对等），并收到不同的涂鸦要求，您需要积极地参与涂鸦，以保证您的分组画板颜色正确数量多于另一支分组。
            </p>
            <p>
              3. 全服共享同一张涂鸦画板，赛事将在每日<span class="info">19:30</span>~<span class="info">20:00</span>之间<span class="info"
                >随机结束</span
              >，结束时，画板正确颜色较多的一组获胜。
            </p>
            <p>4. 获胜的分组人员将平分<span class="gold">奖池金币</span>。</p>
          </div>
        </el-popover>
      </div>
    </h2>
    <el-countdown
      class="countdown"
      format="开赛倒计时：HH时 mm分 ss秒"
      :value="dayjs().hour(19).minute(0).second(0)"
      value-style="font-size: 16px"
      @finish="isBegin = true"
      v-show="!isBegin && !isEnd"
    />
    <p v-show="isBegin && !isEnd" style="text-align: center">赛事进行中</p>
    <p v-show="isEnd" style="text-align: center">今日赛事已结束，{{ winGroup === 0 ? '本轮平局' : winGroup === 1 ? '1 组获胜' : '2 组获胜' }}</p>
    <div class="color-board">
      <template v-if="typeof group !== 'undefined'">
        <span>选择颜色：</span>
        <div class="color-list">
          <div
            class="item-wrap"
            v-for="(item, index) of crazyGraffitiColorList"
            @click="curColorIndex = index + 1"
            :class="{ active: curColorIndex === index + 1 }"
          >
            <div class="item" :style="{ backgroundColor: item }"></div>
          </div>
        </div>
      </template>
      <template v-else>
        <p style="margin: 0; text-align: center; width: 100%">请在下方报名参赛</p>
      </template>
    </div>
    <div class="game">
      <div class="info">
        <p>
          奖池金币：<span class="gold">{{ formatNumberWithCommas(playerCount * 200000) }}</span>
        </p>
        <p>参赛人数：{{ formatNumberWithCommas(playerCount) }}</p>
        <p style="margin-top: 5px">您的分组：{{ group ? `${group} 组` : isBegin ? '（未报名）' : '（未开赛）' }}</p>
        <el-countdown
          format="涂鸦冷却：mm:ss"
          :value="drawTime ? dayjs(drawTime).add(5, 'minute') : 0"
          value-style="font-size: 12px; color: #cfd3dc;"
          style="margin: 10px 0; height: 17px"
        />
        <div>
          <span>涂鸦目标：</span>
          <div class="section-list" v-show="answer" style="margin-top: 5px">
            <div class="item" v-for="item of answer" :style="{ backgroundColor: crazyGraffitiColorList[item - 1] }"></div>
          </div>
          <p v-show="!answer">{{ isBegin ? '（未报名）' : '（未开赛）' }}</p>
        </div>
      </div>
      <div class="section-list">
        <div
          class="item"
          v-for="(item, index) of sectionList"
          :style="{ backgroundColor: crazyGraffitiColorList[item - 1] }"
          @click="handleCrazyGraffitiDraw(index)"
        >
          <span style="color: #fff; opacity: 0.7">{{ index + 1 }}</span>
        </div>
      </div>
    </div>
    <div class="game-info-wrap border-wrap">
      <h4>赛事信息</h4>
      <div class="game-info" id="crazyGraffitiMsgBox">
        <p v-for="item of msgList" v-html="item"></p>
      </div>
    </div>

    <div class="bottom-action">
      <el-button type="primary" plain @click="joinCrazyGraffiti" :disabled="typeof group !== 'undefined' || isBegin || isEnd">
        {{ typeof group === 'undefined' ? '报名' : '已报名' }}
      </el-button>
      <el-button type="success" plain @click="emits('back')" style="margin: 10px 0 0 0">返回</el-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.crazy-graffiti {
  height: 100%;
  display: flex;
  flex-flow: column;
  h2 {
    position: relative;
    .help-info {
      position: absolute;
      top: 0;
      right: 10px;
    }
  }
  .countdown {
    text-align: center;
    margin-bottom: 30px;
  }
  .color-board {
    display: flex;
    margin-bottom: 20px;
    align-items: center;
    span {
      margin-right: 10px;
    }
    .color-list {
      display: flex;
      flex: 1;
      .item-wrap {
        padding: 4px;
        border: 1px var(--fontColor) solid;
        border-radius: 5px;
        margin-right: 10px;
        cursor: pointer;
        background-color: rgba($color: #fff, $alpha: 0.1);
        &.active {
          border-color: red;
        }
        .item {
          height: 16px;
          width: 16px;
        }
      }
    }
  }
  .game {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    padding: 0 5px;
    .info {
      p {
        margin: 6px 0;
        &:first-child {
          margin: 0;
        }
      }
      .section-list {
        grid-template-columns: repeat(3, 20px);
        grid-template-rows: repeat(3, 20px);
      }
    }
    .section-list {
      display: grid;
      grid-template-columns: repeat(3, 60px);
      grid-template-rows: repeat(3, 60px);
      margin-bottom: 10px;
      @media screen and (max-width: 351px) {
        grid-template-columns: repeat(3, 50px);
        grid-template-rows: repeat(3, 50px);
      }
      .item {
        border: 1px var(--fontColor) solid;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
      }
    }
  }
  .game-info-wrap {
    flex: 1;
    margin-bottom: 10px;
    padding: 0 10px 10px 10px;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    .game-info {
      overflow: auto;
      font-size: 14px;
      p {
        line-height: 1.4;
        margin: 10px 0;
      }
    }
  }
  .bottom-action {
    button {
      width: 100%;
    }
  }
}
</style>
